<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <button @click="$router.push('/supplies-manager/master')" class="mb-2 text-slate-500 hover:text-orange-500 transition-colors flex items-center gap-2 group text-xs font-bold uppercase tracking-widest">
            <v-icon small class="group-hover:-translate-x-1 transition-transform" color="inherit">mdi-arrow-left</v-icon>
            Voltar
          </button>
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">
            {{ id ? 'Editar Abastecimento' : 'Novo Abastecimento' }}
          </h1>
          <p class="text-sm text-slate-500 font-medium mt-1">
            Preencha os dados do abastecimento administrativo
          </p>
        </div>
      </div>

      <!-- FORMULÁRIO -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <!-- Divisor Escuro -->
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>mdi-gas-station</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            DADOS DO ABASTECIMENTO
          </h2>
        </div>

        <div class="p-8">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row dense>
              <v-col cols="12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                <v-autocomplete
                  :disabled="$store.getters.getUserData.table_key == 'prefectures' ? true : false"
                  v-model="data.prefecture_id"
                  :items="prefectures"
                  :rules="fieldRules"
                  item-text="prefecture_social_name"
                  item-value="prefecture_id"
                  @change="[getDrivers(data.prefecture_id), getProviders(data.prefecture_id)]"
                  placeholder="Selecione a prefeitura"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                />
              </v-col>
              
              <v-col cols="12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Fornecedor</label>
                <v-autocomplete
                  :rules="fieldRules"
                  :items="providers"
                  @change="loadItems(data.provider_id, data.prefecture_id)"
                  item-value="provider_id"
                  item-text="provider_name"
                  v-model="data.provider_id"
                  placeholder="Selecione o fornecedor"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Item</label>
                <v-autocomplete
                  :rules="fieldRules"
                  :items="items"
                  item-value="item_id"
                  item-text="item_name"
                  v-model="data.item_id"
                  placeholder="Selecione o item/combustível"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                >
                  <template slot="selection" slot-scope="{ item }">
                    <span class="text-sm font-medium">{{ item.item_description_desc }} <span class="text-emerald-600 ml-2">R$ {{ realMaskII(item.item_price.toFixed(3)) }}</span></span>
                  </template>
                  <template slot="item" slot-scope="{ item }">
                    <span class="text-sm">{{ item.item_description_desc }} <span class="text-emerald-600 ml-2">R$ {{ realMaskII(item.item_price.toFixed(3)) }}</span></span>
                  </template>
                </v-autocomplete>
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Motorista Autorizado</label>
                <v-autocomplete
                  :items="drivers"
                  :rules="fieldRules"
                  item-value="driver_id"
                  item-text="driver_name"
                  v-model="data.driver_id"
                  placeholder="Selecione ou clique [+] para adicionar"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                  append-icon="mdi-plus"
                  @click:append="showForm = true"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Litros Autorizados</label>
                <v-text-field
                  v-model="data.supply_authorized_liters"
                  v-mask-decimal.br="3"
                  :disabled="disableLitros"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Valor Autorizado (R$)</label>
                <v-text-field
                  v-model="data.supply_authorized_value"
                  v-mask-decimal.br="2"
                  :disabled="disableValor"
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                />
              </v-col>
              
              <v-col cols="12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Justificativa</label>
                <v-textarea
                  v-model="data.justificativa"
                  :rules="fieldRules"
                  rows="3"
                  auto-grow
                  outlined dense hide-details="auto"
                  class="bg-slate-50"
                  placeholder="Digite o motivo da autorização extra do abastecimento..."
                />
              </v-col>
            </v-row>
          </v-form>
        </div>

        <!-- FOOTER AÇÕES -->
        <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4">
          <button 
            @click="$router.push('/supplies-manager/master')"
            class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase tracking-widest px-4 py-2 transition-colors flex-none"
          >
            Cancelar
          </button>
          <div class="flex-none">
            <button 
              :disabled="!valid || loader"
              @click="validate"
              class="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-[11px] uppercase tracking-widest px-8 h-10 rounded-lg shadow-md shadow-orange-500/20 transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
            >
              <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
              <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
              <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- DIALOG - CADASTRAR MOTORISTA -->
    <v-dialog v-model="showForm" max-width="500" persistent>
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <h3 class="text-white font-bold uppercase tracking-widest text-sm flex items-center gap-2">
            <v-icon color="orange" small>mdi-account-plus</v-icon>
            Cadastrar Motorista
          </h3>
          <button @click="[(showForm = false), (driver_name = '')]" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
            <v-icon small color="inherit">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6">
          <v-form @submit.prevent="newDriver">
            <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Nome do Motorista</label>
            <v-text-field
              :rules="fieldRules"
              v-model="driver_name"
              placeholder="Digite o nome completo do motorista..."
              outlined dense hide-details="auto"
              class="bg-slate-50"
              autofocus
            />
          </v-form>
        </div>

        <!-- Footer -->
        <div class="bg-slate-100 px-6 py-4 flex justify-end gap-3 border-t border-slate-200">
          <button 
            @click="[(showForm = false), (driver_name = '')]" 
            class="px-6 py-2 rounded-lg text-slate-500 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-200 transition-colors"
          >
            CANCELAR
          </button>
          <button 
            :disabled="!driver_name || loader"
            @click="newDriver"
            class="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold uppercase tracking-widest text-[11px] shadow-sm transition-colors flex items-center gap-2"
          >
            <v-icon v-if="loader" small class="mdi-spin" color="white">mdi-loading</v-icon>
            <v-icon v-else small color="white">mdi-check</v-icon>
            CONFIRMAR
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG - COPIAR SENHA -->
    <v-dialog v-model="showPassword" max-width="350" persistent>
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col">
        <!-- Header -->
        <div class="bg-orange-500 px-6 py-4 flex items-center justify-center">
          <h3 class="text-white font-black uppercase tracking-widest text-lg flex items-center gap-2">
            COPIE SUA SENHA
          </h3>
        </div>

        <!-- Body -->
        <div class="p-8 text-center flex flex-col items-center gap-6">
          <v-icon x-large color="emerald" class="mb-2">mdi-check-circle</v-icon>
          <p class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            ABASTECIMENTO GERADO COM A SENHA:
          </p>
          <input
            readonly
            class="text-4xl font-black text-slate-800 text-center bg-slate-100 py-4 px-2 rounded-lg w-full border-2 border-slate-200 focus:outline-none"
            ref="copiar"
            :value="password"
          />
        </div>

        <!-- Footer -->
        <div class="bg-slate-100 px-6 py-4 border-t border-slate-200">
          <button 
            @click="copy"
            :class="copiado ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-800 hover:bg-slate-900 text-white'"
            class="w-full py-3 rounded-lg font-bold uppercase tracking-widest text-xs shadow-sm transition-colors flex items-center justify-center gap-2"
          >
            <v-icon v-if="copiado" small color="white">mdi-check-all</v-icon>
            <v-icon v-else small color="white">mdi-content-copy</v-icon>
            {{ copiado ? "SENHA COPIADA!" : "COPIAR SENHA" }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar multi-line :color="snack.color" v-model="snack.show" transition="fade-transition">
      <span class="font-bold text-sm">{{ snack.message }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  mixins: [Helpers],
  data: () => ({
    showPassword: false,
    password: "",
    copiado: false,
    valid: false,
    loader: false,
    showForm: false,
    providers: [],
    drivers: [],
    vehicles: [],
    prefectures: [],
    items: [],
    driver_name: "",
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      supply_id: null,
      supply_authorized_liters: "0",
      supply_authorized_value: "0",
      supply_status: "PRE-AUTORIZADO",
      supply_adjusted: false,
      supply_extra: true,
      supply_liters_stocked: false,
      supply_billed: false,
      vehicle_id: null,
      driver_id: null,
      item_id: null,
      prefecture_id: null,
      provider_id: null,
      justificativa: null,
    },
  }),
  created() {
    this.Helpers;
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.loader = false;
    this.$http.get("/providers").then((r) => (this.providers = r.data));

    if (this.$store.getters.getUserData.table_key == "prefectures") {
      this.data.prefecture_id = this.$store.getters.getUserData.table_id;
      this.getProviders();
    }

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`supplies/single/${this.id}`).then((r) => {
        this.data = r.data[0];
        this.$http
          .get(`/supplies/providers/${r.data[0].provider_id}`)
          .then((i) => {
            this.items = i.data;
            this.dialog = true;
          });
        delete this.data.datehour;
        delete this.data.supply_liters_stocked;
        delete this.data.supply_price_day;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  computed: {
    disableLitros() {
      return parseFloat(this.data.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.data.supply_authorized_liters.replace(',', '.')) > 0;
    }
  },
  methods: {
    getProviders() {
      this.vehicles = [];
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/providers/prefecture/${this.data.prefecture_id}`)
        .then((r) => {
          this.providers = r.data;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
            this.$router.push("/supplies-manager/master");
          }, 1000);
        } else {
          alert("não copiado!");
        }
      } catch (err) {
        alert(
          "Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C."
        );
      }
    },
    loadItems(provider_id, prefecture_id) {
      this.$http
        .get(`/supplies/providers/prefectures/${provider_id}/${prefecture_id}`)
        .then((r) => (this.items = r.data));
    },
    getDrivers(prefecture_id) {
      this.$http
        .get(`/supplies/drivers/${prefecture_id}`)
        .then((r) => (this.drivers = r.data));
    },
    newDriver() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .post("/supplies/drivers", {
          driver_name: this.driver_name,
          prefecture_id: this.data.prefecture_id,
        })
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.$http.get("/drivers").then((d) => {
            this.drivers = d.data;
            const driverItems = d.data.filter((f) =>
              f.driver_name === this.driver_name ? f : null
            );
            console.log(driverItems[0].driver_id);
            this.data.driver_id = driverItems[0].driver_id;
            this.showForm = false;
          });
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    checkFields() {
      if (this.data.supply_authorized_value) {
        this.data.supply_authorized_liters = 0;
      } else {
        this.data.supply_authorized_value = 0;
      }
    },
    createOrUpdate() {
      this.loader = true;
      // this.checkFields();
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/supplies/single/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push("/supplies-manager/master");
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.data.is_autorized = true;
        this.data.authorizer = this.$store.getters.getUserData.user_login;
        this.$http
          .post("/supplies/single", this.data)
          .then((r) => {
            this.showPassword = true;
            this.$http
              .get(`/supplies/single/${r.data.idInserted}`)
              .then((r) => (this.password = r.data[0].supply_code));
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
